/**
 * Created by Christof Sander on 08.09.2016.
 */

const utils 		= require('./utils')
const appConfig 	= require('./config').getConfig()
const ComponentTree = require('./componentTree')

const CONTAINER_VERSION = (process.env || {}).VERSION || '0.0.0';

// TODO: prevent base structure from being sent to the client

module.exports = {

	// this is the container storage which exists only once per container to share information within a container regardless of the used url.
	container: {
		version: CONTAINER_VERSION,
		persistent: {},
		cache: {
			default: {}, // contains all "container/<someCacheName>" cache scopes
		},
	},
	// this is the list of service dependent data (the key is <serviceDomain>)
	apps: {},
	// this is the list of service dependent data (the key is <mediaFolder>)
	medias: {},
	// this is a list of sessions (the key is the sessionID)
	sessions: {},
	// this is a list of sessionID dependent request storages
	requests: {},
	// this is a list of componentTree instances for each service (the key is <realServiceDomain>) NOTE: means api-auth-domain
	componentTrees: {},

	// this is a list of default values used for server side entries.
	defaults: {
		app: {
			version: CONTAINER_VERSION,
			nrOfActiveRequests: 0,
			nrOfPausedRequests: 0,
			privileges: {},
			categories: {
				own: {},
				foreign: {}
			},
			service: {},
			persistent: {
				resetPasswords: {},
				// register of pending activations used by activateCredentials and registerCredentials
				activationRegister: {},
				// register of shortLinks (e.g., used for sending short urls via sms)
				shortLinkRegister: {},
			},
			fullAppInits: {},
			routes: {},
			cache: {
				gateways: 			{},
				defaultScope: 		{},
				routesScope: 		{},
				tagsScope: 			{},
				menusScope: 		{},
				categoriesScope: 	{},
				forwards: 			{}
			},
			// project dependent, non-persistent storage
			project: {},
			// list of scopeNames declared within the actual app (um_scopes)
			scopes: {},
		},
		media: {
			persistent: {
				proxyCache: {
					// stores last init of proxyCache
					initTS: 0,
					// register of all proxyCache content definitions and dependencies
					register: {},
				},
			},
		},
		session: {
			usageCounter: 0,
			user: {
				environment: {},
			},
			data: {
				cookiePolicy: {
					acceptTracking: null,
				},
			},
		},
		request: {
			preparedRequestList: {},
			preparedRequestCollectionList: {}
		},
	},

	// this is an object list of storage elements (the key is <sessionID>_<serviceDomain>)
	// NOTE: a storage object contains references to the original data within the other lists and some extra data (like sessionID)
	storages: {},

	/**
	 * this function will be called once on store init and should be overwritten by client- or server-specific code.
	 */
	init: function(){
		// NOTE: overwritten by client || server
	},

	/**
	 * this function will be called after fec has initialized the services and should be overwritten by client- or server-specific code.
	 */
	onServiceInit: function(storage){
		// NOTE: overwritten by client || server
	},

	/**
	 * this function will be called before each store.set and should be overwritten by client- or server-specific code.
	 */
	beforeSet: function(){
		// NOTE: overwritten by client || server
	},

	/**
	 * this function will be called after each store.set and should be overwritten by client- or server-specific code.
	 */
	afterSet: function(){
		// NOTE: overwritten by client || server
	},

	/**
	 * prepare mediaFolder dependent store according to the given serviceDomain.
	 * this method returns a valid mediaStore even if the service is not initialized and therefor the mediaFolder not present yet.
	 * fallbackMediaFolder is used as value and will be returned but not set within storage.media.folder.
	 * therefor, if storage.media.folder is set the service has been initialized.
	 * in contrast to the session and request stores the media store is present and usable at the client but will not be sent to the client.
	 *
	 * @param serviceDomain		string - service domain
	 * @returns {string}		used mediaFolder
	 */
	initMediaStore: function(serviceDomain){
		// initialize mediaFolder dependent storage
		// if the service is not ready yet a default mediaStorage will be prepared but gets no service dependent flags.
		let mediaFolder = 'fallbackMediaFolder', serviceMediaInitGranted = false
		const serviceHealth = ((this.apps[serviceDomain] || {}).service || {}).health || {}
		if(serviceHealth.success){
			const serviceMediaFolder = (this.apps[serviceDomain].service || {}).mediaFolder
			if(serviceMediaFolder){
				mediaFolder = serviceMediaFolder
				serviceMediaInitGranted = true
			}
		}
		if(!(this.medias[mediaFolder] || {}).initialized){
			const newMedia = utils.simpleCopy(this.defaults.media)
			utils.refReplace(newMedia.persistent, (this.medias[mediaFolder] || {}).persistent || {})
			newMedia.initialized = true
			newMedia.initTS = new Date().getTime()

			if(serviceMediaInitGranted){
				newMedia.folder = mediaFolder
			}

			this.medias[mediaFolder] = newMedia
		}

		return mediaFolder
	},

	/**
	 * this function returns the storage according to a given sessionID and serviceDomain.
	 * NOTE: this function should no be called externally. Use get-method instead.
	 *
	 * @param   requestID               			string - requestID (can be empty if used on client-side)
	 * @param   sessionID               			string - sessionID (can be empty if used on client-side)
	 * @param   serviceDomain           			string - serviceDomain (can be empty if used on client-side)
	 * @param   [getStorageInitParamsCallback]		function - if a new storage (session) gets initialized this function will be called to retrieve the current device the client is using and the langCode the client wants to use.
	 * @param   [incrementUsageCounter=false]		boolean - should the usageCounter be incremented or not
	 * @param   [copyUserFromSessionID=''] 			string - may be a sessionID from which the user should be copied. only used for serverSide purposes (e.g., like route-reallocation).
	 * @returns object - storage
	 */
	getStorage: function(requestID, sessionID, serviceDomain, getStorageInitParamsCallback = null, incrementUsageCounter = false, copyUserFromSessionID = ''){
		if(sessionID){
			if(!this.sessions[sessionID]){
				this.sessions[sessionID] = utils.simpleCopy(this.defaults.session)
			}
			if(incrementUsageCounter){
				this.sessions[sessionID].usageCounter++
			}
			if(requestID){
				if(!this.requests[sessionID]){
					this.requests[sessionID] = {}
				}
				if(!this.requests[sessionID][requestID]){
					this.requests[sessionID][requestID] = utils.simpleMerge(this.defaults.request, { requestID, referenceTS: new Date().toISOString() })
				}
			}
		}

		if(serviceDomain){
			const storageKey = `${requestID || 'request'}_${sessionID || 'session'}_${serviceDomain}`
			let realServiceDomain, serviceDomainFound = false

			if(appConfig.isServer){
				if(appConfig.services[serviceDomain]){
					serviceDomainFound = true
					realServiceDomain = appConfig.services[serviceDomain]['api-auth-domain']
				} else {
					console.error('SERVICE_DOMAIN_NOT_FOUND', serviceDomain, appConfig.services)
					realServiceDomain = 'UNKNOWN_API_AUTH_DOMAIN'
				}
			}
			else {
				serviceDomainFound = true
				realServiceDomain = serviceDomain
			}

			if(!this.apps[serviceDomain] || !this.apps[serviceDomain].initialized){
				// initialize app by defaults without killing persistent app storage
				const newApp = utils.simpleCopy(this.defaults.app)
				utils.refReplace(newApp.persistent, (this.apps[serviceDomain] || {}).persistent || {})
				this.apps[serviceDomain] = newApp
				this.apps[serviceDomain].initialized = true
				this.apps[serviceDomain].upStartTimestamp = this.upStartTimestamp
				this.apps[serviceDomain].serviceDomainFound = serviceDomainFound
			}

			// initialize mediaFolder dependent storage
			const mediaFolder = this.initMediaStore(serviceDomain)

			let isStorageInit = false
			if(!(this.storages[storageKey] || {}).app){
				isStorageInit = true

				if(!this.componentTrees[realServiceDomain]){
					this.componentTrees[realServiceDomain] = new ComponentTree()
				}

				this.storages[storageKey] = {
					container:			this.container,
					app:                this.apps[serviceDomain],
					media:              this.medias[mediaFolder],
					componentTree:      this.componentTrees[realServiceDomain],
					sessionID:          sessionID,
					session:            this.sessions[sessionID] || {},
					requestID:          requestID,
					request: 			(this.requests[sessionID] || {})[requestID] || {},
					// storage should know if it is a server- or a client-storage
					isServer:           !!this.isServer,
					isClient:           !!this.isClient,
					realServiceDomain:  realServiceDomain,
					serviceDomain:  	serviceDomain,
				}
			}

			return this.sanitizeStorage(this.storages[storageKey], getStorageInitParamsCallback, isStorageInit, copyUserFromSessionID)
		}

		// TODO: THROW ERROR?
		return {}
	},

	/**
	 * this function is used to overwrite the data stored within one specific storage.
	 * NOTE: this function should no be called externally. Use set-method instead.
	 *
	 * @param   data            					object - data to write to the store
	 * @param   requestID       					string - requestID (can be empty if used on client-side)
	 * @param   sessionID       					string - sessionID (can be empty if used on client-side)
	 * @param   serviceDomain   					string - serviceDomain (can be empty if used on client-side)
	 * @param   [getStorageInitParamsCallback]		function - if a new storage (session) gets initialized this function will be called to retrieve the current device the client is using and the langCode the client wants to use.
	 * @param   [resetApp=false]   					boolean - should the app be reset by defaults
	 * @param   [updateServer=false]				boolean - should the server be updated according to the given data
	 * @param   [updateServerCallback]				object - optional - { function: <callbackFunction>, context: <callbackContext> }
	 * @returns storage
	 */
	setStorage: function(data, requestID, sessionID, serviceDomain, getStorageInitParamsCallback = null, resetApp = false, updateServer = false, updateServerCallback = null){
		const storage = this.getStorage(requestID, sessionID, serviceDomain, getStorageInitParamsCallback)

		// never set usageCounter according to real request session data
		// NOTE:    this usageCounter is used to prevent closeSession-calls from killing the "in-request-store-entries"
		//          in case of multiple parallel requests sent within one session
		if(data && data.session) delete data.session.usageCounter;

		// reset app value if necessary
		// TODO: storage.app.persistent data too?
		if(resetApp){
			utils.refReplace(storage.app, utils.simpleCopy(this.defaults.app || {}))
		}

		// request storage cannot be valid across different requests even within the same session.
		storage.request = {
			...(data.request || {}),
			requestID: 		(storage.request || {}).requestID,
			referenceTS: 	(storage.request || {}).referenceTS
		}

		this.beforeSet(storage, data)
		utils.refMerge(storage, data)
		this.afterSet(storage, updateServer, updateServerCallback)
		return this.sanitizeStorage(storage)
	},

	/**
	 * this function returns the actual storage according to the given parameters.
	 *
	 * @param   requestID               			string - requestID (can be empty if used on client-side)
	 * @param   sessionID               			string - sessionID (can be empty if used on client-side)
	 * @param   serviceDomain           			string - serviceDomain (can be empty if used on client-side)
	 * @param   [getStorageInitParamsCallback]		function - if a new storage (session) gets initialized this function will be called to retrieve the current device the client is using and the langCode the client wants to use.
	 * @param   [incrementUsageCounter=false] 		boolean - should the usageCounter be incremented or not
	 * @param   [copyUserFromSessionID] 			string - may be a sessionID from which the user should be copied. only used for serverSide purposes (e.g., like route-reallocation).
	 * @returns storage
	 */
	get: function(requestID, sessionID, serviceDomain, getStorageInitParamsCallback = null, incrementUsageCounter = false, copyUserFromSessionID = ''){
		sessionID = sessionID || appConfig.sessionIDFallback
		requestID = requestID || appConfig.requestIDFallback
		serviceDomain = utils.hostToDomain(serviceDomain || appConfig.serviceDomainFallback)

		return this.getStorage(requestID, sessionID, serviceDomain, getStorageInitParamsCallback, incrementUsageCounter, copyUserFromSessionID)
	},

	/**
	 * this function is used to write some data to the storage identified by the given parameters.
	 *
	 * @param   data            					object - data to write to the store
	 * @param   requestID       					string - requestID (can be empty if used on client-side)
	 * @param   sessionID       					string - sessionID (can be empty if used on client-side)
	 * @param   serviceDomain   					string - serviceDomain (can be empty if used on client-side)
	 * @param   [getStorageInitParamsCallback]      function - if a new storage (session) gets initialized this function will be called to retrieve the current device the client is using and the langCode the client wants to use.
	 * @param   [resetApp=false]                    boolean - should the app be reset by defaults
	 * @param   [updateServer=true]                	boolean - should the servers' storage be updated according to the given data
	 * @param   [updateServerCallback]              object - { function: <callbackFunction>, context: <callbackContext> }
	 * @returns storage
	 */
	set: function(data, requestID, sessionID, serviceDomain, getStorageInitParamsCallback = null, resetApp = false, updateServer = true, updateServerCallback = null){
		sessionID = sessionID || appConfig.sessionIDFallback
		requestID = requestID || appConfig.requestIDFallback
		serviceDomain = utils.hostToDomain(serviceDomain || appConfig.serviceDomainFallback)

		return this.setStorage(data, requestID, sessionID, serviceDomain, getStorageInitParamsCallback, !!resetApp, updateServer === undefined || !!updateServer, updateServerCallback)
	},

	/**
	 * this function is used to remove the data stored within the given storage and
	 * the corresponding reference lists.
	 *
	 * TODO: serviceDomain not in use?!
	 *
	 * @param   storage         					object - actual storage
	 * @param   sessionID       					string - sessionID (can be empty if used on client-side)
	 * @param   serviceDomain   					string - serviceDomain (can be empty if used on client-side)
	 * @param   [deleteThisStorageOnly=false] 		boolean - should only the given storage or all storages (according to the sessionID) be deleted?
	 */
	remove: function(storage, sessionID, serviceDomain, deleteThisStorageOnly){
		utils.forEach(storage, function(element, elementKey){
			if(elementKey != 'componentTree') delete storage[elementKey];
		});
		if(!deleteThisStorageOnly && sessionID){
			var self = this;
			utils.forEach(self.storages, function(otherStorage, otherStorageKey){
				if((otherStorage || {}).sessionID === sessionID){
					self.remove(self.storages[otherStorageKey], sessionID, serviceDomain, true);
				}
			});
			delete this.sessions[sessionID];
		}
		delete storage;
	},

	/**
	 * this function is used to remove the request dependent data stored within the given storage and
	 * the corresponding reference lists.
	 *
	 * @param   storage 		object - actual storage
	 */
	removeRequest: function(storage){
		if((storage || {}).requestID && storage.sessionID){
			// remove this request from store
			if((this.requests[storage.sessionID] || {})[storage.requestID]){
				delete this.requests[storage.sessionID][storage.requestID];
			}

			// remove empty requests within this session from store
			if(this.requests[storage.sessionID] && Object.keys(this.requests[storage.sessionID] || {}).length <= 0) {
				delete this.requests[storage.sessionID];
			}

			// remove this requests' storage from store
			var storageKey = (storage.requestID || 'request') + '_' + (storage.sessionID || 'session') + '_' + storage.serviceDomain;
			if(this.storages[storageKey]){
				delete this.storages[storageKey];
			}
		}
	},

	/**
	 * remove request as well as session parts of the given storage from memory.
	 *
	 * @param storage	object - storage to remove
	 */
	removeAll(storage){
		this.removeRequest(storage)
		this.remove(storage, storage.sessionID)
	},
};